export interface WindowAttentionOptions {
  onVisible?: boolean;
  onFocus?: boolean;
  onOnline?: boolean;
  minIntervalMs?: number;
}

export interface VisibilityReturnOptions {
  minHiddenDurationMs: number;
}

export interface VisibilityChangeOptions {
  onVisible?: () => void | Promise<void>;
  onHidden?: () => void | Promise<void>;
}

export interface NetworkStatusOptions {
  onOnline?: () => void | Promise<void>;
  onOffline?: () => void | Promise<void>;
}

/**
 * Register a deduplicated "window attention" handler.
 * Coalesces visibility/focus/online bursts so callers can share one execution path.
 */
export function attachWindowAttentionListeners(
  onAttention: () => void | Promise<void>,
  options: WindowAttentionOptions = {}
): () => void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => {};
  }

  const {
    onVisible = true,
    onFocus = true,
    onOnline = true,
    minIntervalMs = 250,
  } = options;

  let lastRunAt = 0;
  let running = false;

  const run = async () => {
    const now = Date.now();
    if (running || now - lastRunAt < minIntervalMs) return;

    running = true;
    try {
      await onAttention();
      lastRunAt = Date.now();
    } finally {
      running = false;
    }
  };

  const handleVisible = () => {
    if (document.visibilityState === 'visible') {
      void run();
    }
  };
  const handleFocus = () => { void run(); };
  const handleOnline = () => { void run(); };

  if (onVisible) document.addEventListener('visibilitychange', handleVisible);
  if (onFocus) window.addEventListener('focus', handleFocus);
  if (onOnline) window.addEventListener('online', handleOnline);

  return () => {
    if (onVisible) document.removeEventListener('visibilitychange', handleVisible);
    if (onFocus) window.removeEventListener('focus', handleFocus);
    if (onOnline) window.removeEventListener('online', handleOnline);
  };
}

/**
 * Register a visibility listener that fires only when the tab returns to visible
 * after being hidden for at least minHiddenDurationMs.
 */
export function attachVisibilityReturnListener(
  onReturnVisible: (hiddenDurationMs: number) => void | Promise<void>,
  options: VisibilityReturnOptions
): () => void {
  if (typeof document === 'undefined') {
    return () => {};
  }

  const { minHiddenDurationMs } = options;
  let hiddenAt: number | null = null;

  const handleVisibilityChange = () => {
    if (document.hidden) {
      hiddenAt = Date.now();
      return;
    }

    if (!hiddenAt) {
      return;
    }

    const hiddenDurationMs = Date.now() - hiddenAt;
    hiddenAt = null;
    if (hiddenDurationMs < minHiddenDurationMs) {
      return;
    }

    void onReturnVisible(hiddenDurationMs);
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);
  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
}

/**
 * Register a visibility listener with explicit visible/hidden callbacks.
 */
export function attachVisibilityChangeListener(
  options: VisibilityChangeOptions
): () => void {
  if (typeof document === 'undefined') {
    return () => {};
  }

  const { onVisible, onHidden } = options;

  const handleVisibilityChange = () => {
    if (document.hidden) {
      if (onHidden) void onHidden();
      return;
    }
    if (onVisible) void onVisible();
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);
  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
}

/**
 * Register online/offline listeners with centralized add/remove wiring.
 */
export function attachNetworkStatusListeners(
  options: NetworkStatusOptions
): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const { onOnline, onOffline } = options;
  const handleOnline = () => { if (onOnline) void onOnline(); };
  const handleOffline = () => { if (onOffline) void onOffline(); };

  if (onOnline) window.addEventListener('online', handleOnline);
  if (onOffline) window.addEventListener('offline', handleOffline);

  return () => {
    if (onOnline) window.removeEventListener('online', handleOnline);
    if (onOffline) window.removeEventListener('offline', handleOffline);
  };
}
